import { useCallback, useMemo } from "react";

import { useNavigate } from "react-router-dom";

import SearchParams from "simplydo/core/searchParams";
import { Invitation, UseInvitationOptions } from "simplydo/interfaces";

import api from "api";

import { useUserReload } from "../useUserReload";
import {
  acceptTokenInvitation,
  invalidateInvitationLists,
  toastError,
} from "./internal";
import { useInvitation } from "./useInvitation";

export const useTokenInvitation = (
  invitation: Invitation,
  options?: UseInvitationOptions,
) => {
  const vm = useInvitation(invitation, options);
  const navigate = useNavigate();
  const reloadUser = useUserReload();

  const token = useMemo(() => {
    if (typeof window === "undefined" || !window.location) {
      return null;
    }
    return new SearchParams(window.location.search).get("invite_token");
  }, []);

  const navigateAfterAccept = useCallback(() => {
    switch (invitation.forType) {
      case "group":
      case "requestJoinGroup":
        navigate(`/groups/${invitation.forId}`);
        break;
      case "idea":
      case "requestJoinIdea":
        navigate(`/ideas/${invitation.forId}`);
        break;
      case "projectInvited":
        if (
          invitation.forType === "projectInvited" &&
          invitation.idea?.challenge
        ) {
          navigate(
            `/challenges/${invitation.idea.challenge}/ideas?queryView=board&idea=${invitation.forId}`,
          );
        }
        break;
      case "ideaAssessor":
        navigate(`/challenges/${invitation.forId}/assessments/assigned`);
        break;
      case "ideaBusinessProfile":
        navigate(`/`);
        break;
      default:
        navigate(`/invitations`);
    }
  }, [invitation, navigate]);

  const acceptInvite = useCallback(() => {
    if (!token) {
      return;
    }
    acceptTokenInvitation(
      token,
      () => {
        api.setQueryData(api.invitations.get, [], (prev) => ({
          ...prev,
          invitations: (prev?.invitations || []).filter(
            (i) => i._id !== invitation._id,
          ),
        }));
        invalidateInvitationLists();
        reloadUser(() => {
          if (
            invitation.forType === "group" ||
            invitation.forType === "requestJoinGroup"
          ) {
            api.invalidateQueries(api.groups.get, [invitation.forId]);
            api.invalidateQueries(api.pages.getAll, [
              "group",
              invitation.forId,
            ]);
            api.invalidateQueries(api.discussions.getAll, [
              "group",
              invitation.forId,
            ]);
          }
          navigateAfterAccept();
        });
      },
      () => toastError("Error accepting invitation"),
    );
  }, [invitation, navigateAfterAccept, reloadUser, token]);

  return useMemo(
    () => ({
      ...vm,
      acceptInvite: token ? acceptInvite : undefined,
      rejectInvite: undefined,
      removeInvite: undefined,
      showDismiss: false,
    }),
    [acceptInvite, token, vm],
  );
};
